import { PlusOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Col, Row, Table, TableColumnsType, TableProps, Tag, Typography } from "antd";
import type { SorterResult } from "antd/lib/table/interface";
import axios from "axios";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { NotificationJob } from "@bindeleddet/core/types";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const PushNotificationJobsDashboard = () => {
  const history = useHistory();
  const { width } = useViewport();

  const [jobs, setJobs] = useState<NotificationJob[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const fetchJobs = (page = 1, pageSize = 5, search = "", ordering = "") => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    if (ordering) {
      params.append("ordering", ordering);
    }

    axios
      .get<PaginatedResponse<NotificationJob>>(`/push-notifications/jobs/?${params.toString()}`)
      .then(response => {
        setJobs(response.data.results);
        setPagination(prev => ({
          ...prev,
          total: response.data.count,
        }));
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke hente push-notifikasjoner. Prøv igjen senere.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchJobs(1, pagination.pageSize, "", "-created_at");
  }, [pagination.pageSize]);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    fetchJobs(1, pagination.pageSize, newSearchTerm);
  };

  type OnChange = NonNullable<TableProps<NotificationJob>["onChange"]>;
  type Sorts = SorterResult<NotificationJob>;

  const [sortedInfo, setSortedInfo] = useState<Sorts>({
    columnKey: "created_at",
    field: "created_at",
    order: "descend",
  } as Sorts);

  const handleChange: OnChange = (tablePagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
    const ordering =
      sorter && !Array.isArray(sorter) && sorter.order ? `${sorter.order === "descend" ? "-" : ""}${sorter.field}` : "";

    setPagination(prev => ({
      ...prev,
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 10,
    }));

    fetchJobs(tablePagination.current || 1, tablePagination.pageSize || 10, searchTerm, ordering);
  };

  const getStatusTag = (status: NotificationJob["status"]) => {
    const statusConfig = {
      queued: { color: "default", text: "I kø" },
      sending: { color: "processing", text: "Sender" },
      sent: { color: "success", text: "Sendt" },
      failed: { color: "error", text: "Feilet" },
    };
    const config = statusConfig[status] || statusConfig.queued;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Parse recipient groups and group by study program shortname
  const parseRecipientGroups = (groups: string[]): Array<{ shortname: string; yearLevels: number[] }> => {
    const grouped: Record<string, number[]> = {};

    groups.forEach(group => {
      const parts = group.split("-");
      if (parts.length === 2) {
        const shortname = parts[0];
        const yearLevel = parseInt(parts[1], 10);
        if (!Number.isNaN(yearLevel)) {
          if (!grouped[shortname]) {
            grouped[shortname] = [];
          }
          grouped[shortname].push(yearLevel);
        }
      }
    });

    return Object.entries(grouped)
      .map(([shortname, yearLevels]) => ({
        shortname,
        yearLevels: yearLevels.sort((a, b) => a - b),
      }))
      .sort((a, b) => a.shortname.localeCompare(b.shortname));
  };

  const renderRecipientGroups = (groups: string[]) => {
    if (groups.length === 0) return <span style={{ color: "#999" }}>Ingen</span>;

    const parsed = parseRecipientGroups(groups);

    return (
      <div>
        {parsed.map(({ shortname, yearLevels }) => (
          <Row key={shortname} style={{ marginBottom: "8px", alignItems: "center" }}>
            <Col style={{ marginRight: "8px", minWidth: "60px" }}>
              <Text>{shortname}</Text>
            </Col>
            <Col>
              <Row>
                {[1, 2, 3, 4, 5].map(yearLevel => (
                  <Col style={{ padding: "0.1em" }} key={yearLevel}>
                    <Badge
                      count={yearLevel}
                      style={{
                        backgroundColor: yearLevels.includes(yearLevel)
                          ? "var(--bindeleddet-color)"
                          : "var(--neutral-alter)",
                        color: yearLevels.includes(yearLevel) ? "var(--neutral)" : "var(--neutral-alt)",
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const columns: TableColumnsType<NotificationJob> = [
    {
      title: "Opprettet",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "created_at" ? sortedInfo.order : null,
      render: (text: string) => {
        const date = dayjs(text);
        return date.format("DD.MM.YYYY HH:mm");
      },
      width: 150,
    },
    {
      title: "Tittel",
      key: "title",
      dataIndex: "title",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "title" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "status" ? sortedInfo.order : null,
      render: (value: unknown) => getStatusTag(value as NotificationJob["status"]),
      width: 120,
    },
    {
      title: "Arrangement",
      key: "event",
      dataIndex: "event_name",
      render: (value: unknown, record: NotificationJob) => {
        const eventName = value as string | null;
        if (!record.event_id || !eventName) return <span style={{ color: "#999" }}>Ingen</span>;
        return <Link to={`/admin/events/${record.event_id}/`}>{eventName}</Link>;
      },
      ellipsis: true,
      width: 200,
    },
    {
      title: "Mottakere",
      key: "recipient_groups",
      dataIndex: "recipient_groups",
      render: (value: unknown) => renderRecipientGroups(value as string[]),
      width: 250,
    },
    {
      title: "Resultat",
      key: "result",
      render: (_, record: NotificationJob) => {
        if (record.status === "sent" || record.status === "failed") {
          const total = record.sent_success_count + record.sent_failure_count;
          if (total === 0) return <span style={{ color: "#999" }}>Ingen sendt</span>;
          return (
            <span>
              {record.sent_success_count} sendt
              {record.sent_failure_count > 0 && (
                <span style={{ color: "#ff4d4f" }}> / {record.sent_failure_count} feilet</span>
              )}
            </span>
          );
        }
        return <span style={{ color: "#999" }}>-</span>;
      },
      width: 150,
    },
    {
      title: "Sendt",
      key: "sent_at",
      dataIndex: "sent_at",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "sent_at" ? sortedInfo.order : null,
      render: (value: unknown) => {
        const text = value as string | null;
        if (!text) return <span style={{ color: "#999" }}>-</span>;
        const date = dayjs(text);
        return date.format("DD.MM.YYYY HH:mm");
      },
      width: 150,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2} style={{ marginRight: "10px" }}>
            Push-notifikasjoner
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push("/admin/tools/push-notifications/send/")}
          >
            Send push-notifikasjon
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            dataSource={jobs}
            onChange={handleChange}
            loading={loading}
            pagination={pagination}
            rowKey="id"
            locale={{
              emptyText: "Ingen push-varsler funnet",
            }}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Tittel",
                dataIndex: "title",
                key: "title",
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (value: unknown) => getStatusTag(value as NotificationJob["status"]),
              },
              {
                title: "Opprettet",
                dataIndex: "created_at",
                key: "created_at",
                render: (value: unknown) => dayjs(value as string).format("DD.MM.YYYY HH:mm"),
              },
            ]}
            onClick={_id => {
              // No detail page yet - placeholder for future implementation
            }}
            data={jobs}
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize,
                }));
                fetchJobs(page, pageSize, searchTerm);
              },
            }}
            emptyText="Ingen push-varsler funnet"
          />
        )}
      </Card>
    </>
  );
};

export default PushNotificationJobsDashboard;
